import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext'; // 1. IMPORTAR useAuth
import { MagnifyingGlassIcon, ChevronDownIcon, LockClosedIcon } from '@heroicons/react/24/solid'; // 2. IMPORTAR LockClosedIcon
import { basharToolsData } from '../../data/basharToolsData'; // Asegúrate que la ruta sea correcta
import CommentSection from '../../components/comments/CommentSection';

// --- Helper: Genera un ID amigable para el scroll ---
const slugify = (text) => {
    return text
        .toString()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w-]+/g, '')
        .replace(/--+/g, '-')
        .replace(/^-+/, '')
        .replace(/-+$/, '');
};

// --- Helper Component: Video Player (MODIFICADO) ---
const ToolPlayer = ({ tool, user }) => {
    const isLocked = tool.requiresAuth && !user;

    if (tool.type === 'youtube' || tool.type === 'video') {
        return (
            <div className="relative aspect-video w-full rounded-lg overflow-hidden border border-gray-700 mt-4">
                {isLocked && (
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center z-20 p-4">
                        <LockClosedIcon className="w-12 h-12 text-cyan-400 mb-3" />
                        <h4 className="text-xl font-bold text-white text-center">Members Only Tool</h4>
                    </div>
                )}
                {tool.type === 'youtube' ? (
                    <iframe
                        className="w-full h-full"
                        src={`https://www.youtube.com/embed/${tool.youtubeId}`}
                        title={tool.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    ></iframe>
                ) : (
                    <video key={tool.videoUrl} className="w-full h-full" controls={!isLocked} playsInline preload="metadata">
                        <source src={tool.videoUrl} type="video/mp4" />
                        Your browser does not support the video tag.
                    </video>
                )}
            </div>
        );
    }
    if (tool.type === 'audio') {
        return (
            <div className="mt-4 p-4 bg-gray-800 rounded-lg border border-gray-700">
                {isLocked ? (
                    <div className="text-center text-gray-400">
                        <LockClosedIcon className="w-8 h-8 mx-auto mb-2 text-cyan-400" />
                        <p>Members Only Audio</p>
                    </div>
                ) : (
                    <audio controls className="w-full">
                        <source src={tool.audioUrl} type="audio/mpeg" />
                        Tu navegador no soporta el elemento de audio.
                    </audio>
                )}
            </div>
        );
    }
    return null;
};

const AudioToolCard = ({ tool, user }) => {
    const isLocked = tool.requiresAuth && !user;
    return (
        <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-800 shadow-lg">
            <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-cyan-900/20 rounded-full">
                    <tool.Icon className={`w-8 h-8 ${tool.iconColor}`} />
                </div>
                <div>
                    <h3 className="text-xl font-bold text-white">{tool.title}</h3>
                    <p className="text-gray-400">{tool.description}</p>
                </div>
            </div>
            
            {/* Aquí iría el reproductor de audio que definimos antes */}
            <div className="bg-black/40 p-4 rounded-lg border border-gray-700">
                {isLocked ? (
                    <div className="flex items-center justify-center gap-2 text-amber-500">
                        <LockClosedIcon className="w-5 h-5" />
                        <span>Premium Audio Track</span>
                    </div>
                ) : (
                    <audio controls className="w-full">
                        <source src={tool.audioUrl} type="audio/mpeg" />
                    </audio>
                )}
            </div>
                              <CommentSection 
                                  threadId={tool.id} 
                              />
        </div>
    );
};

// --- Helper Component: Card for Videos (MODIFICADO) ---
const VideoToolCard = ({ tool, user }) => {
    const isLocked = tool.requiresAuth && !user;
    return (
        <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-800 shadow-lg flex flex-col">
            <div className="flex items-start gap-4">
                <div className="relative flex-shrink-0">
                    <tool.Icon className={`w-10 h-10 ${tool.iconColor}`} />
                    {isLocked && (
                        <div className="absolute -bottom-1 -right-1 flex items-center justify-center w-5 h-5 bg-black/80 backdrop-blur-sm rounded-full border border-amber-500/50">
                            <LockClosedIcon className="w-3 h-3 text-amber-400" />
                        </div>
                    )}
                </div>
                <div>
                    <h3 className="text-xl font-bold text-white">{tool.title}</h3>
                    <p className="text-gray-400 mt-1">{tool.description}</p>
                </div>
            </div>
            <ToolPlayer tool={tool} user={user} />
            
                              
                              <CommentSection 
                                  threadId={tool.id} 
                              />
        </div>
    );
};

// --- Helper Component: Card for Internal Links (MODIFICADO) ---
const InternalLinkToolCard = ({ tool, user }) => {
    const isLocked = tool.requiresAuth && !user;

    const CardContent = () => (
        <>
            <div className="flex items-start gap-4 mb-4">
                <div className="relative flex-shrink-0">
                    <tool.Icon className={`w-8 h-8 ${tool.iconColor}`} />
                    {isLocked && (
                        <div className="absolute -bottom-1 -right-1 flex items-center justify-center w-5 h-5 bg-black/80 backdrop-blur-sm rounded-full border border-amber-500/50">
                            <LockClosedIcon className="w-3 h-3 text-amber-400" />
                        </div>
                    )}
                </div>
                <div>
                    <h2 className={`text-xl font-bold transition-colors duration-200 leading-tight ${isLocked ? 'text-gray-500' : 'text-white group-hover:text-cyan-400'}`}>{tool.title}</h2>
                </div>
            </div>
            <p className={`text-base mb-6 line-clamp-3 ${isLocked ? 'text-gray-600' : 'text-gray-400'}`}>{tool.description}</p>
            {!isLocked && (
                <div className="flex items-center justify-end text-cyan-400 font-semibold group-hover:text-cyan-300 transition-colors duration-200">
                    <span className="mr-2">Access Resource</span>
                </div>
            )}
            
                              
        </>
    );

    const lockedClasses = "bg-gray-900/60 rounded-xl shadow-lg p-6 relative border border-gray-800 cursor-not-allowed";
    const unlockedClasses = "block bg-gray-800 rounded-xl shadow-xl hover:shadow-cyan-500/10 transition-all duration-300 p-6 relative group transform hover:-translate-y-1 border border-gray-800 hover:border-cyan-700";

    if (isLocked) {
        return <div className={lockedClasses}><CardContent /></div>;
    }

    return (
        <Link to={tool.pathUrl} className={unlockedClasses}>
            <CardContent />
        </Link>
    );
};


// --- Main Component ---
const BasharTools = () => {
    const { user } = useAuth(); // 3. OBTENER EL USUARIO
    const [searchTerm, setSearchTerm] = useState('');
    const [openCategory, setOpenCategory] = useState(basharToolsData[0]?.category.id || null);

    useEffect(() => {
        document.documentElement.style.scrollBehavior = 'smooth';
        return () => {
            document.documentElement.style.scrollBehavior = 'auto';
        };
    }, []);

    const filteredTools = useMemo(() => {
        if (!searchTerm.trim()) return basharToolsData;
        return basharToolsData.filter(tool =>
            tool.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            tool.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            tool.category.title.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm]);

    const groupedToolsForIndex = useMemo(() => {
        return filteredTools.reduce((acc, tool) => {
            const category = tool.category;
            if (!acc[category.id]) {
                acc[category.id] = {
                    title: category.title,
                    Icon: category.Icon,
                    iconColor: category.iconColor,
                    tools: []
                };
            }
            acc[category.id].tools.push(tool);
            return acc;
        }, {});
    }, [filteredTools]);


    const handleToggleCategory = (categoryId) => {
        setOpenCategory(prev => (prev === categoryId ? null : categoryId));
    };

    return (
        <div className="relative bg-black text-white min-h-screen font-sans">
            <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-black to-gray-900 opacity-95 z-0"></div>

            <main className="relative z-10 container mx-auto px-4 py-16">
                <h1 style={{ fontFamily: '"Cinzel Decorative", serif' }} className="text-4xl sm:text-5xl font-bold text-center mb-4 text-cyan-300">
                    Bashar's Essential Tools
                </h1>
                <p className="text-lg sm:text-xl text-gray-400 text-center max-w-3xl mx-auto mb-12">
                    Select a tool from the index below or scroll to explore.
                </p>

                <div className="max-w-xl mx-auto mb-8">
                     <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 pointer-events-none" />
                        <input
                            type="text"
                            placeholder="Search all tools..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-gray-900/70 border border-gray-700 text-white rounded-lg pl-11 pr-4 py-3 focus:ring-2 focus:ring-cyan-500 focus:outline-none transition"
                        />
                    </div>
                </div>

                {/* --- ÍNDICE DE HERRAMIENTAS COLAPSABLE (MODIFICADO) --- */}
                <div className="max-w-xl mx-auto w-full space-y-2 mb-20">
                    {Object.entries(groupedToolsForIndex).length > 0 ? (
                        Object.entries(groupedToolsForIndex).map(([categoryId, categoryData]) => (
                            <div key={categoryId} className="bg-gray-900/50 border border-gray-800 rounded-lg">
                                <button
                                    onClick={() => handleToggleCategory(categoryId)}
                                    className="w-full flex justify-between items-center p-3 text-left hover:bg-gray-800/60"
                                >
                                    <div className="flex items-center gap-3">
                                        <categoryData.Icon className={`w-5 h-5 ${categoryData.iconColor} flex-shrink-0`} />
                                        <h2 className="text-lg font-semibold text-gray-200">{categoryData.title}</h2>
                                    </div>
                                    <ChevronDownIcon
                                        className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${openCategory === categoryId ? 'rotate-180' : ''}`}
                                    />
                                </button>
                                {openCategory === categoryId && (
                                    <div className="border-t border-gray-800">
                                        <ul className="py-2">
                                            {categoryData.tools.map(tool => {
                                                const isLocked = tool.requiresAuth && !user;
                                                const linkClasses = isLocked ? "cursor-default" : "hover:bg-cyan-900/50 hover:text-white";
                                                
                                                return (
                                                <li key={tool.title}>
                                                    <a
                                                        href={isLocked ? undefined : `#${slugify(tool.title)}`}
                                                        onClick={(e) => isLocked && e.preventDefault()}
                                                        className={`flex items-center gap-3 text-gray-300 transition-colors duration-200 text-sm px-4 py-2 ${linkClasses}`}
                                                    >
                                                        <div className="relative flex-shrink-0">
                                                            <tool.Icon className="w-4 h-4 text-white/70" />
                                                            {isLocked && (
                                                                <div className="absolute -bottom-1.5 -right-1.5 flex items-center justify-center w-3.5 h-3.5 bg-gray-800/75 rounded-full border border-amber-600/70">
                                                                    <LockClosedIcon className="w-2 h-2 text-amber-400" />
                                                                </div>
                                                            )}
                                                        </div>
                                                        
                                                        <span className="flex-grow">{tool.title}</span>

                                                        {isLocked && (
                                                            <span className="ml-auto text-xs font-semibold text-amber-500/90 bg-amber-900/60 px-2 py-0.5 rounded-full">
                                                                Members Only
                                                            </span>
                                                        )}
                                                    </a>
                                                </li>
                                            )})}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <p className="text-center text-gray-400 py-4">No tools found for your search.</p>
                    )}
                </div>

                {/* --- CONTENIDO PRINCIPAL: TARJETAS DE HERRAMIENTAS (MODIFICADO) --- */}
                <div className="max-w-5xl mx-auto space-y-12">
                    {filteredTools.length > 0 ? (
                        filteredTools.map(tool => (
                            <div key={slugify(tool.title)} id={slugify(tool.title)} className="scroll-mt-20">
                              {/* Lógica de renderizado según tipo */}
                              {tool.type === 'youtube' || tool.type === 'video' ? (
                                  <VideoToolCard tool={tool} user={user} />
                              ) : tool.type === 'audio' ? (
                                  /* Aquí agregamos el nuevo componente para audios */
                                  <AudioToolCard tool={tool} user={user} />
                              ) : (
                                  /* Por defecto, tarjetas de enlaces */
                                  <InternalLinkToolCard tool={tool} user={user} />
                              )}
                          </div>
                        ))
                    ) : (
                        !searchTerm && (
                            <div className="text-center py-16 px-6 bg-gray-900/50 rounded-lg border border-gray-800">
                                 <p className="text-2xl font-semibold text-gray-400">No tools available.</p>
                            </div>
                        )
                    )}
                </div>
            </main>
            <footer className="relative z-10 text-center py-6 text-xs text-gray-600">
                © {new Date().getFullYear()} Gus • Aguiar | Tools for transformation.
            </footer>
        </div>
    );
};

export default BasharTools;